import React, { useState, useEffect } from 'react';
import { Home, Monitor, Layers, Download, CreditCard, MessageCircle, Sun, Moon } from 'lucide-react';
import { useRouter } from '../context/RouterContext';
import { useTheme } from '../context/ThemeContext';
import contentData from '../data/website_content.json';

export const MobileBottomNav: React.FC = () => {
  const { currentPath, navigate } = useRouter();
  const { theme, toggleTheme } = useTheme();
  const { brand } = contentData;
  const leadDev = brand.developers[0];
  const [activeTab, setActiveTab] = useState<string>('home');

  useEffect(() => {
    if (currentPath === '/') {
      const handleHashChange = () => {
        const hash = window.location.hash;
        if (hash === '#showcase') setActiveTab('showcase');
        else if (hash === '#features') setActiveTab('features');
        else setActiveTab('home');
      };
      window.addEventListener('hashchange', handleHashChange);
      handleHashChange();
      return () => window.removeEventListener('hashchange', handleHashChange);
    } else if (currentPath === '/download') {
      setActiveTab('download');
    } else if (currentPath === '/features') {
      setActiveTab('features');
    } else if (currentPath === '/pricing') {
      setActiveTab('pricing');
    } else {
      setActiveTab(currentPath);
    }
  }, [currentPath]);

  const handleNav = (tabId: string, action: () => void) => {
    setActiveTab(tabId);
    action();
  };

  const navItems = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      action: () => {
        if (currentPath !== '/') navigate('/');
        else window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
    {
      id: 'showcase',
      label: 'Screens',
      icon: Monitor,
      action: () => {
        if (currentPath !== '/') {
          navigate('/');
          setTimeout(() => {
            document.getElementById('showcase')?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          document.getElementById('showcase')?.scrollIntoView({ behavior: 'smooth' });
        }
      },
    },
    {
      id: 'features',
      label: 'Features',
      icon: Layers,
      action: () => {
        if (currentPath === '/') {
          document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate('/features');
        }
      },
    },
    {
      id: 'download',
      label: 'Download',
      icon: Download,
      action: () => {
        navigate('/download');
      },
    },
    {
      id: 'pricing',
      label: 'Pricing',
      icon: CreditCard,
      action: () => {
        navigate('/pricing');
      },
    },
  ];

  return (
    <nav 
      aria-label="Mobile Bottom Navigation"
      className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-surface/95 backdrop-blur-2xl border-t border-border shadow-elevated px-2 py-1.5 transition-colors"
    >
      <div className="flex items-center justify-around max-w-md mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNav(item.id, item.action)}
              className={`flex flex-col items-center justify-center py-1 px-2.5 rounded-xl transition-all relative ${
                isActive
                  ? 'text-rose font-bold scale-105'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {isActive && (
                <span className="absolute -top-1 w-6 h-1 rounded-full bg-rose" />
              )}
              <Icon className="w-5 h-5 mb-0.5" />
              <span className="text-[10px] font-mono tracking-tight">{item.label}</span>
            </button>
          );
        })}

        {/* WhatsApp Direct Chat Tab */}
        <a
          href={leadDev.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center justify-center py-1 px-2 rounded-xl text-emerald-400 hover:text-emerald-300 transition-all"
          title="WhatsApp Support"
        >
          <div className="relative">
            <MessageCircle className="w-5 h-5 mb-0.5" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <span className="text-[10px] font-mono font-bold">Chat</span>
        </a>

        {/* Quick Theme Toggle on Mobile Tab */}
        <button
          onClick={toggleTheme}
          aria-label="Toggle dark/light theme"
          className="flex flex-col items-center justify-center py-1 px-2 rounded-xl text-slate-400 hover:text-slate-200 transition-all"
        >
          {theme === 'dark' ? (
            <Sun className="w-4 h-4 mb-0.5 text-amber-400" />
          ) : (
            <Moon className="w-4 h-4 mb-0.5 text-slate-700" />
          )}
          <span className="text-[9px] font-mono uppercase">{theme}</span>
        </button>
      </div>
    </nav>
  );
};
